import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2, Info, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const BOOL_FIELD = ({ label, value, onChange, testId, helperText }) => (
  <label className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg cursor-pointer hover:border-slate-300 transition" data-testid={testId}>
    <div>
      <p className="text-sm font-medium text-slate-700">{label}</p>
      {helperText && <p className="text-xs text-slate-500 mt-0.5">{helperText}</p>}
    </div>
    <div className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-orange-500' : 'bg-slate-300'}`}
      onClick={(e) => { e.preventDefault(); onChange(!value); }}>
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-5' : ''}`} />
    </div>
  </label>
);

export default function ExtendedProfileFields({ workerType }) {
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchExtended = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tradespeople/profile/extended`, { withCredentials: true });
      setFields(res.data || {});
    } catch { /* not created yet */ }
    setLoaded(true);
  }, []);

  useEffect(() => { fetchExtended(); }, [fetchExtended]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/tradespeople/profile/extended`, fields, { withCredentials: true });
      toast.success('Extended profile saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, value) => setFields(f => ({ ...f, [key]: value }));

  if (!loaded) return null;

  // For individual_tradesperson, show ONLY the small "core trade" set.
  const isIndividual = workerType === 'individual_tradesperson' || !workerType;

  const moderationStatus = fields.moderation_status;

  return (
    <div className="space-y-5 mt-6 p-5 bg-slate-50 border border-slate-200 rounded-xl" data-testid="extended-profile-section">
      {/* Moderation Banner */}
      {moderationStatus && (
        <div className={`p-3 rounded-lg border text-sm ${
          moderationStatus === 'flagged_for_review' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
          moderationStatus === 'needs_changes' ? 'bg-orange-50 border-orange-200 text-orange-800' :
          moderationStatus === 'rejected_agency' ? 'bg-red-50 border-red-200 text-red-800' :
          moderationStatus === 'suspended' ? 'bg-red-50 border-red-200 text-red-800' :
          'bg-slate-50 border-slate-200 text-slate-700'
        }`} data-testid="moderation-banner">
          {moderationStatus === 'flagged_for_review' && (
            <p><strong>Profile under review.</strong> Your profile is being reviewed by our team. It will be visible to employers once approved.</p>
          )}
          {moderationStatus === 'needs_changes' && (
            <p><strong>Changes requested.</strong> {fields.moderation_reason || 'Please update your profile based on the feedback below.'} Edit your profile and save to resubmit for review.</p>
          )}
          {moderationStatus === 'rejected_agency' && (
            <p><strong>Profile not approved.</strong> {fields.moderation_reason || 'Your profile has been flagged as a labour agency or third-party supplier, which is not permitted on OnSiteHand.'} If this is a mistake, please edit your profile to clarify that you carry out work directly.</p>
          )}
          {moderationStatus === 'suspended' && (
            <p><strong>Profile suspended.</strong> {fields.moderation_reason || 'Your profile has been suspended.'} Please contact support for more information.</p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        <h4 className="text-base font-bold text-slate-900">Additional Details</h4>
        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
          {workerType === 'individual_tradesperson' && 'Individual'}
          {workerType === 'self_employed_subcontractor' && 'Subcontractor'}
          {workerType === 'small_trade_team_gang' && 'Trade Team'}
          {workerType === 'limited_company_trade_contractor' && 'Ltd Company'}
        </span>
      </div>
      <p className="text-xs text-slate-500 -mt-3">
        These optional fields help employers understand your setup. CIS, VAT and insurance information may be self-declared unless marked as verified. Employers remain responsible for checking suitability, documents, CIS/tax treatment, contracts and site requirements.
      </p>

      {/* === SHARED: Work setup (every worker type) === */}
      <div className="space-y-4" data-testid="ext-shared-work-setup">
        <h5 className="text-sm font-semibold text-slate-800">Work setup</h5>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Years of experience</label>
            <input type="number" min="0" max="80" value={fields.years_experience ?? ''} onChange={e => set('years_experience', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-years-experience" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Main trade</label>
            <input type="text" value={fields.main_trade || ''} onChange={e => set('main_trade', e.target.value)}
              placeholder="e.g. Plumbing" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-main-trade" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Additional trades <span className="text-xs text-slate-400">Comma separated</span></label>
            <input type="text"
              value={Array.isArray(fields.additional_trades) ? fields.additional_trades.join(', ') : (fields.additional_trades || '')}
              onChange={e => set('additional_trades', e.target.value)}
              placeholder="e.g. Heating, Boilers"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-additional-trades" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Work radius (miles)</label>
            <input type="number" min="0" max="500" value={fields.work_radius_miles ?? ''} onChange={e => set('work_radius_miles', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-work-radius" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Preferred work type</label>
            <select multiple value={Array.isArray(fields.preferred_work_type) ? fields.preferred_work_type : []}
              onChange={e => set('preferred_work_type', Array.from(e.target.selectedOptions).map(o => o.value))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-preferred-work-type">
              <option value="labour_only">Labour only</option>
              <option value="supply_and_fit">Supply and fit</option>
              <option value="price_work">Price work</option>
              <option value="day_rate">Day rate</option>
              <option value="emergency_callout">Emergency callout</option>
            </select>
            <p className="text-[11px] text-slate-400 mt-1">Hold Ctrl/Cmd to select multiple.</p>
          </div>
        </div>
        <BOOL_FIELD label="Available evenings?" value={fields.available_evenings || false} onChange={v => set('available_evenings', v)} testId="ext-shared-evenings" />
        <BOOL_FIELD label="Available weekends?" value={fields.available_weekends || false} onChange={v => set('available_weekends', v)} testId="ext-shared-weekends" />
      </div>

      {/* === SELF-EMPLOYED SUBCONTRACTOR === */}
      {workerType === 'self_employed_subcontractor' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Trading Name <span className="text-xs text-slate-400">Optional</span></label>
            <input type="text" value={fields.trading_name || ''} onChange={e => set('trading_name', e.target.value)}
              placeholder="e.g. Smith Plastering" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-trading-name" />
          </div>
          <BOOL_FIELD label="CIS Registered?" value={fields.cis_registered || false} onChange={v => set('cis_registered', v)} testId="ext-cis" helperText="Self-declared" />
          <div>
            <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
              <Lock className="h-3.5 w-3.5 text-slate-400" /> UTR Number <span className="text-xs text-slate-400">Private - not shown publicly</span>
            </label>
            <input type="text" value={fields.utr || ''} onChange={e => set('utr', e.target.value)}
              placeholder="Unique Taxpayer Reference" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-utr" />
          </div>
          <BOOL_FIELD label="Can price work?" value={fields.can_price_work || false} onChange={v => set('can_price_work', v)} testId="ext-can-price" />
          <BOOL_FIELD label="Labour only?" value={fields.labour_only || false} onChange={v => set('labour_only', v)} testId="ext-labour-only" />
          <BOOL_FIELD label="Supply and fit?" value={fields.supply_and_fit || false} onChange={v => set('supply_and_fit', v)} testId="ext-supply-fit" />
          <BOOL_FIELD label="Own tools?" value={fields.own_tools || false} onChange={v => set('own_tools', v)} testId="ext-own-tools" />
          <BOOL_FIELD label="Own transport?" value={fields.own_transport || false} onChange={v => set('own_transport', v)} testId="ext-own-transport" />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Public Liability Insurance <span className="text-xs text-slate-400">Recommended</span></label>
            <input type="url" value={fields.public_liability_insurance_url || ''} onChange={e => set('public_liability_insurance_url', e.target.value)}
              placeholder="Upload URL or link to certificate" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-pli-url" />
          </div>
        </div>
      )}

      {/* === SMALL TRADE TEAM / GANG === */}
      {workerType === 'small_trade_team_gang' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Team/Gang Name <span className="text-xs text-slate-400">Optional</span></label>
            <input type="text" value={fields.team_name || ''} onChange={e => set('team_name', e.target.value)}
              placeholder="e.g. Cooper & Sons Crew" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-team-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Team lead name <span className="text-xs text-slate-400">Optional</span></label>
            <input type="text" value={fields.team_lead_name || ''} onChange={e => set('team_lead_name', e.target.value)}
              placeholder="e.g. Joe Cooper" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-team-lead-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Number of Workers Available</label>
            <input type="number" min="1" value={fields.number_of_workers_available || ''} onChange={e => set('number_of_workers_available', e.target.value)}
              placeholder="e.g. 4" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-num-workers" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Trades Covered <span className="text-xs text-slate-400">Comma separated</span></label>
            <input type="text" value={Array.isArray(fields.trades_covered) ? fields.trades_covered.join(', ') : (fields.trades_covered || '')}
              onChange={e => set('trades_covered', e.target.value)} placeholder="e.g. Bricklaying, Groundwork, Labouring"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-trades-covered" />
          </div>
          <BOOL_FIELD label="Can provide same team regularly?" value={fields.can_provide_same_team_regularly || false} onChange={v => set('can_provide_same_team_regularly', v)} testId="ext-same-team" />
          <BOOL_FIELD label="Team has transport?" value={fields.team_has_transport || false} onChange={v => set('team_has_transport', v)} testId="ext-team-has-transport" />
          <BOOL_FIELD label="Team has tools?" value={fields.team_has_tools || false} onChange={v => set('team_has_tools', v)} testId="ext-team-has-tools" />
          <BOOL_FIELD label="Own tools?" value={fields.own_tools || false} onChange={v => set('own_tools', v)} testId="ext-own-tools" />
          <BOOL_FIELD label="Own transport?" value={fields.own_transport || false} onChange={v => set('own_transport', v)} testId="ext-own-transport" />
          <BOOL_FIELD label="Available evenings?" value={fields.available_evenings || false} onChange={v => set('available_evenings', v)} testId="ext-evenings" />
          <BOOL_FIELD label="Available weekends?" value={fields.available_weekends || false} onChange={v => set('available_weekends', v)} testId="ext-weekends" />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Public Liability Insurance <span className="text-xs text-slate-400">Recommended</span></label>
            <input type="url" value={fields.public_liability_insurance_url || ''} onChange={e => set('public_liability_insurance_url', e.target.value)}
              placeholder="Upload URL or link" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-pli-url" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Employers' Liability Insurance <span className="text-xs text-slate-400">Recommended if you bring workers</span></label>
            <input type="url" value={fields.employers_liability_insurance_url || ''} onChange={e => set('employers_liability_insurance_url', e.target.value)}
              placeholder="Upload URL or link" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-eli-url" />
          </div>
        </div>
      )}

      {/* === LIMITED COMPANY TRADE CONTRACTOR === */}
      {workerType === 'limited_company_trade_contractor' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Registered Company Name</label>
            <input type="text" value={fields.registered_company_name || ''} onChange={e => set('registered_company_name', e.target.value)}
              placeholder="e.g. Smith Construction Ltd" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-company-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company Number <span className="text-xs text-slate-400">Optional</span></label>
            <input type="text" value={fields.company_number || ''} onChange={e => set('company_number', e.target.value)}
              placeholder="e.g. 12345678" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-company-number" />
          </div>
          <BOOL_FIELD label="VAT Registered?" value={fields.vat_registered || false} onChange={v => set('vat_registered', v)} testId="ext-vat" />
          <div>
            <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
              <Lock className="h-3.5 w-3.5 text-slate-400" /> VAT Number <span className="text-xs text-slate-400">Private — admin/verification only</span>
            </label>
            <input type="text" value={fields.vat_number || ''} onChange={e => set('vat_number', e.target.value)}
              placeholder="e.g. GB123456789" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-vat-number" />
            <p className="text-[11px] text-slate-400 mt-1">This is kept private and used for verification/admin checks only.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Number of Workers Available</label>
            <input type="number" min="1" value={fields.number_of_workers_available || ''} onChange={e => set('number_of_workers_available', e.target.value)}
              placeholder="e.g. 6" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-num-workers" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Trades Covered <span className="text-xs text-slate-400">Comma separated</span></label>
            <input type="text" value={Array.isArray(fields.trades_covered) ? fields.trades_covered.join(', ') : (fields.trades_covered || '')}
              onChange={e => set('trades_covered', e.target.value)} placeholder="e.g. Roofing, Scaffolding"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-trades-covered" />
          </div>
          <BOOL_FIELD label="Can price work?" value={fields.can_price_work || false} onChange={v => set('can_price_work', v)} testId="ext-can-price" />
          <BOOL_FIELD label="Labour only?" value={fields.labour_only || false} onChange={v => set('labour_only', v)} testId="ext-labour-only" />
          <BOOL_FIELD label="Supply and fit?" value={fields.supply_and_fit || false} onChange={v => set('supply_and_fit', v)} testId="ext-supply-fit" />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Public Liability Insurance <span className="text-xs text-slate-400">Recommended</span></label>
            <input type="url" value={fields.public_liability_insurance_url || ''} onChange={e => set('public_liability_insurance_url', e.target.value)}
              placeholder="Upload URL or link" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-pli-url" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Employers' Liability Insurance <span className="text-xs text-slate-400">Recommended if you bring workers</span></label>
            <input type="url" value={fields.employers_liability_insurance_url || ''} onChange={e => set('employers_liability_insurance_url', e.target.value)}
              placeholder="Upload URL or link" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ext-eli-url" />
          </div>
        </div>
      )}

      {/* Save button */}
      <button onClick={handleSave} disabled={saving}
        className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
        data-testid="ext-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {saving ? 'Saving...' : 'Save Additional Details'}
      </button>
    </div>
  );
}
